import { Feature, Geometry, Position } from 'geojson';

const METERS_PER_FOOT = 0.3048;

export function feetToMeters(feet: number): number {
    return feet * METERS_PER_FOOT;
}

export function metersToFeet(meters: number): number {
    return meters / METERS_PER_FOOT;
}

function positionHasAltitude(position: Position): boolean {
    return position.length >= 3 && typeof position[2] === 'number' && Number.isFinite(position[2]);
}

export function hasAltitude(geometry: Geometry | null | undefined): boolean {
    if (!geometry) {
        return false;
    }

    switch (geometry.type) {
        case 'Point':
            return positionHasAltitude(geometry.coordinates);
        case 'MultiPoint':
        case 'LineString':
            return geometry.coordinates.some(positionHasAltitude);
        case 'MultiLineString':
            return geometry.coordinates.some((line) => line.some(positionHasAltitude));
        case 'Polygon':
            return geometry.coordinates.some((ring) => ring.some(positionHasAltitude));
        case 'MultiPolygon':
            return geometry.coordinates.some((polygon) => polygon.some((ring) => ring.some(positionHasAltitude)));
        case 'GeometryCollection':
            return geometry.geometries.some(hasAltitude);
        default:
            return false;
    }
}

const ELEVATABLE_GEOMETRY_TYPES = new Set(['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon']);

export interface ElevationSplitResult {
    flat: Feature[];
    elevated: Feature[];
}

export function splitFeaturesByElevation(features: Feature[]): ElevationSplitResult {
    const flat: Feature[] = [];
    const elevated: Feature[] = [];

    for (const feature of features) {
        const geometryType = feature.geometry?.type;
        if (geometryType && ELEVATABLE_GEOMETRY_TYPES.has(geometryType) && hasAltitude(feature.geometry)) {
            elevated.push(feature);
        } else {
            flat.push(feature);
        }
    }

    return { flat, elevated };
}
